import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { forkJoin, Observable } from 'rxjs';
import { UtilsHelper } from '@helpers/utils.helper';
import { Device } from '@models/device';
import { UpdateAttribute } from '@models/updateAttribute.model';
import { AutoCorrectionSettings, UpdateAutoCorrectionPayload } from '@models/auto-correction.model';
import { AttributeNewDeviceService } from '@services/attribute-new-device.service'; // importa el servicio
import { SessionService } from '@services/session.service';
import { ToastService } from '@shared/services/toast.service';

const DAILY_GRID_MINUTES = 1440;

@Component({
  standalone: false,
  selector: 'app-stack-settings',
  templateUrl: './stack-settings.component.html',
  styleUrls: ['./stack-settings.component.scss'],
})

export class StackSettingsComponent implements OnInit, OnChanges {
  @Input() device!: Device;
  @Output() updated = new EventEmitter<void>();

  stackForm!: FormGroup;
  submitted = false;
  initialFormValue: any;
  availableSensors: { id: number; name: string; icon?: string }[][] = [];

  autoCorrectionLoading = false;
  autoCorrectionError = false;

  constructor(
    private fb: FormBuilder,
    private attributeService: AttributeNewDeviceService,
    private toastService: ToastService,
    private sessionService: SessionService,

  ) { }

  // Autocorrección de horario: funcionalidad restringida a superadmin (ni
  // admin ni usuario normal) — coherente con la restricción real que aplica
  // el backend (@SetMetadata('roles', [UserRoleType.SUPERADMIN]) en
  // devices.controller.ts). Ocultar aquí es solo UX; quien la aplica de
  // verdad es el guard del backend.
  get isSuperAdmin(): boolean {
    return this.sessionService.isSuperAdmin();
  }

  ngOnInit() {
    if (this.device) {
      this.buildForm();
      this.updateFormStateByConnectivity();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['device'] && this.device) {
      this.buildForm();
      this.updateFormStateByConnectivity();
      // Solo aquí, no en ngOnInit: con un @Input() ligado desde el padre,
      // Angular ejecuta ngOnChanges (con el valor inicial incluido) antes que
      // ngOnInit — ponerlo en los dos duplicaba la llamada HTTP.
      this.loadAutoCorrection();
    }
  }

  private buildForm(): void {
    const formControlsConfig: any = {
      sendPeriod: [
        parseInt(
          this.device?.extraInfo?.newPeriod?.value ||
          this.device?.extraInfo?.period?.value ||
          '15',
          10
        ),
        [Validators.required, Validators.min(1), this.validAutoCorrectionPeriod],
      ],
      // Se rellenan de verdad al resolver loadAutoCorrection() (llamada aparte,
      // no depende de extraInfo) — aquí solo valores por defecto para que el
      // formulario exista desde el primer render.
      enableAutoCorrection: [false],
      sendTime: [0, [this.validSendTime]],
    };

    this.device?.sensors?.forEach((sensor, index) => {
      const nodeKey = `node${index + 1}`;
      const sondaKey = `sonda${index + 1}`;

      const newPeriodValue = sensor.extraInfo?.newPeriod?.value;
      const fallbackPeriodValue = sensor.extraInfo?.period?.value;
      const period = parseInt(newPeriodValue || fallbackPeriodValue || '60', 10);

      // módulo del node
      const moduleConnected = sensor.extraInfo?.moduleConnected?.value ?? '0';
      this.availableSensors[index] =
        UtilsHelper.getAvailableSensorsByModule(moduleConnected);


      const currentSensorInterno =
        sensor.extraInfo?.newProbeConnected?.value ??
        sensor.extraInfo?.probeConnected?.value;

      formControlsConfig[nodeKey] = [
        period,
        [Validators.required, Validators.min(60)]
      ];

      formControlsConfig[sondaKey] = [
        Number(currentSensorInterno),
        Validators.required
      ];
    });


    this.stackForm = this.fb.group(formControlsConfig);
    this.initialFormValue = this.deepCopy(this.stackForm.value);
  }

  // Con la rejilla horaria, un período solo cae siempre en hora en punto si
  // divide 60 (5, 10, 15, 20, 30) o es múltiplo de 60 (120, 180, 240...).
  // Cualquier otro valor (81, 90, 210...) produce marcas irregulares — ver
  // docs/plans/2026-07-06-alineacion-horaria-stacks.md. Solo aplica si la
  // autocorrección está activa: sin ella, "Período envío" no tiene esta
  // restricción (comportamiento previo intacto).
  private validAutoCorrectionPeriod(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    const value = control.value;
    if (!parent || !value) return null;

    const autoCorrectionEnabled = parent.get('enableAutoCorrection')?.value;
    if (!autoCorrectionEnabled) return null;

    const isValid = 60 % value === 0 || value % 60 === 0;
    return isValid ? null : { invalidAutoCorrectionPeriod: true };
  }

  // La casilla es hermana de "sendPeriod" en el mismo FormGroup: el
  // validador de arriba no se re-evalúa solo porque esta cambie, hay que
  // forzarlo explícitamente.
  onAutoCorrectionToggle(): void {
    this.stackForm.get('sendPeriod')?.updateValueAndValidity();
  }

  // Cuando el período es menor de 60, "sendTime" solo tiene sentido real
  // entre 0 y período-1 — cualquier valor igual o mayor se "envuelve"
  // (5 con período 3 equivale a 2), lo que confunde más de lo que aclara.
  // Con la rejilla diaria (isDailyGrid) no aplica, ahí sendTime son minutos
  // desde medianoche (0-1439) y sí representa una hora real del día.
  private validSendTime(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    const value = control.value;
    if (!parent || value == null) return null;

    const autoCorrectionEnabled = parent.get('enableAutoCorrection')?.value;
    const sendPeriod = parent.get('sendPeriod')?.value;
    if (!autoCorrectionEnabled || !sendPeriod || sendPeriod >= DAILY_GRID_MINUTES) return null;

    return value < sendPeriod ? null : { sendTimeWrapsAround: true };
  }

  // "sendTime" depende de "sendPeriod" (su máximo válido es período-1) —
  // hay que revalidarlo cuando el período cambia, por el mismo motivo que
  // onAutoCorrectionToggle().
  onSendPeriodChange(): void {
    this.stackForm.get('sendTime')?.updateValueAndValidity();
  }

  get maxSendTime(): number {
    const sendPeriod = this.stackForm?.get('sendPeriod')?.value;
    return sendPeriod > 0 ? sendPeriod - 1 : 59;
  }

  // Llamada separada de la carga normal del dispositivo (nunca se mezcla con
  // loadExtraInfo): si el servidor UDP falla o tarda, solo afecta a esta
  // sección, no al resto de la ficha.
  private loadAutoCorrection(): void {
    if (!this.device?.id) return;
    // El backend ya la rechaza para no-superadmin (403) — nos ahorramos la
    // petición de más y evitamos disparar autoCorrectionError para roles que
    // ni siquiera deben ver esta sección.
    if (!this.isSuperAdmin) return;
    // Un dispositivo en modo Bluetooth nunca va a tener stack en el servidor
    // UDP — la llamada siempre acabaría en 404. isBluetoothMode refleja la
    // conexión actual, no si el hardware tiene NB-IoT de fábrica — si el
    // componente se vuelve a renderizar con datos ya actualizados a NBIOT
    // (ngOnChanges), esta función se reevalúa y sí lanza la llamada.
    if (this.isBluetoothMode) return;

    this.autoCorrectionLoading = true;
    this.autoCorrectionError = false;

    this.attributeService.getAutoCorrection(this.device.id).subscribe({
      next: (settings: AutoCorrectionSettings) => {
        this.autoCorrectionLoading = false;

        const patch: any = {
          enableAutoCorrection: settings.enableAutoCorrection,
          sendTime: settings.sendTime,
        };
        // Mientras está activa, "Periodo envío" deja de mostrar el período
        // fluctuante y pasa a mostrar el objetivo fijo.
        if (settings.enableAutoCorrection && settings.periodCorrectedMinutes != null) {
          patch.sendPeriod = settings.periodCorrectedMinutes;
        }

        this.stackForm.patchValue(patch, { emitEvent: false });
        this.initialFormValue = this.deepCopy(this.stackForm.value);
        this.updateFormStateByConnectivity();
      },
      error: (err) => {
        this.autoCorrectionLoading = false;
        // 404 = este dispositivo no tiene stack en el servidor UDP (tarjeta
        // NB-IoT nunca dada de alta ahí) — es un caso esperado, no un fallo
        // real, así que no mostramos el aviso de error para este caso.
        if (err?.status !== 404) {
          this.autoCorrectionError = true;
        }
      }
    });
  }

  get isDailyGrid(): boolean {
    return this.stackForm?.get('sendPeriod')?.value === DAILY_GRID_MINUTES;
  }

  // ion-input type="time" trabaja con strings "HH:mm"; sendTime se guarda
  // como minutos desde medianoche — este getter/setter hace de puente.
  get sendTimeAsHHMM(): string {
    const minutes = this.stackForm?.get('sendTime')?.value ?? 0;
    const hh = Math.floor(minutes / 60).toString().padStart(2, '0');
    const mm = (minutes % 60).toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  onSendTimeHHMMChange(value: string): void {
    if (!value) return;
    const [hh, mm] = value.split(':').map((part) => parseInt(part, 10));
    this.stackForm.get('sendTime')?.setValue((hh * 60) + mm);
  }

  get autoCorrectionPreview(): string {
    const enabled = this.stackForm?.get('enableAutoCorrection')?.value;
    if (!enabled) return '';

    const period = this.stackForm?.get('sendPeriod')?.value;
    const sendTime = this.stackForm?.get('sendTime')?.value ?? 0;

    if (this.isDailyGrid) {
      const hh = Math.floor(sendTime / 60).toString().padStart(2, '0');
      const mm = (sendTime % 60).toString().padStart(2, '0');
      return `Se enviará una vez al día, a las ${hh}:${mm}.`;
    }

    if (!period || period <= 0) return '';

    // Explicación genérica del campo "minuto", con horas concretas
    // enumeradas (no notación "xx:MM") e independiente de la configuración
    // actual — sirve como referencia aunque el período no sea "cada hora".
    return 'El minuto que elijas se repite en cada marca. Por ejemplo, con el minuto 0: 1:00h, 2:00h, 3:00h. Con el minuto 5: 1:05h, 2:05h, 3:05h.';
  }

  get nodeControls() {
    return this.device?.sensors?.map((_, i) => this.stackForm.get(`node${i + 1}`)) || [];
  }

  getUpdatedNodePeriod(index: number): number {
    const value = this.stackForm?.get(`node${index}`)?.value;
    return value ? value * 2 : 0;
  }

  get calculatedSendPeriod(): number {
    const val = this.stackForm?.get('sendPeriod')?.value;
    return val ? val * 2 : 0;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.stackForm.invalid) return;

    const attributesPayload = this.generateChangedAttributes();
    const autoCorrectionPayload = this.generateAutoCorrectionChanges();

    const requests: Observable<any>[] = [];
    if (attributesPayload.length) {
      requests.push(this.attributeService.updateAttributes(attributesPayload));
    }
    if (autoCorrectionPayload) {
      requests.push(this.attributeService.updateAutoCorrection(this.device.id, autoCorrectionPayload));
    }

    if (!requests.length) return;

    forkJoin(requests).subscribe({
      next: () => {
        this.updated.emit();
        this.initialFormValue = this.deepCopy(this.stackForm.value);
        this.stackForm.markAsPristine();
        this.toastService.successToast('Dispositivo actualizado');
      },
      error: (err) => {
        this.toastService.errorToast('Ha habido un error al actualizar los datos. Inténtelo de nuevo más tarde');
      }
    });
  }

  // Con la autocorrección activa, "sendPeriod" ya no viaja por aquí (newPeriod)
  // — se guarda como periodCorrectedMinutes vía generateAutoCorrectionChanges().
  // Nunca se mandan los dos caminos a la vez para el mismo campo.
  private generateAutoCorrectionChanges(): UpdateAutoCorrectionPayload | null {
    const changes: UpdateAutoCorrectionPayload = {};
    let hasChanges = false;

    const currentEnabled = this.stackForm.get('enableAutoCorrection')?.value;
    if (currentEnabled !== this.initialFormValue['enableAutoCorrection']) {
      changes.enableAutoCorrection = currentEnabled;
      hasChanges = true;
    }

    if (currentEnabled) {
      // Al activar la casilla (transición false→true) forzamos el envío del
      // objetivo aunque "sendPeriod"/"sendTime" no hayan cambiado en este
      // guardado: initialFormValue.sendPeriod puede llevar el valor ya
      // sincronizado por un guardado manual previo (con la autocorrección
      // aún desactivada), lo que hacía que la comparación de abajo nunca
      // detectara cambio y periodCorrectedMinutes se quedara en null.
      const isBeingEnabledNow = currentEnabled !== this.initialFormValue['enableAutoCorrection'];

      const currentSendPeriod = this.stackForm.get('sendPeriod')?.value;
      if (isBeingEnabledNow || currentSendPeriod !== this.initialFormValue['sendPeriod']) {
        changes.periodCorrectedMinutes = currentSendPeriod;
        hasChanges = true;
      }

      const currentSendTime = this.stackForm.get('sendTime')?.value;
      if (isBeingEnabledNow || currentSendTime !== this.initialFormValue['sendTime']) {
        changes.sendTime = currentSendTime;
        hasChanges = true;
      }
    }

    return hasChanges ? changes : null;
  }

  private generateChangedAttributes(): UpdateAttribute[] {
    const changes: UpdateAttribute[] = [];

    // Verificar cambios en sendPeriod — solo por este camino si la
    // autocorrección NO está activa (si lo está, va por periodCorrectedMinutes).
    const currentSend = this.stackForm.get('sendPeriod')?.value;
    const initialSend = this.initialFormValue['sendPeriod'];
    const autoCorrectionActive = this.stackForm.get('enableAutoCorrection')?.value;

    if (!autoCorrectionActive && currentSend !== initialSend) {
      changes.push({
        id: this.device.id,
        type: '0',
        attribute: 'newPeriod',
        value: String(currentSend)
      });
    }

    // Verificar cambios en nodos
    this.device?.sensors?.forEach((sensor, index) => {
      const controlKey = `node${index + 1}`;
      const currentValue = this.stackForm.get(controlKey)?.value;
      const initialValue = this.initialFormValue[controlKey];

      if (currentValue !== initialValue) {
        changes.push({
          id: sensor.id,
          type: '1',
          attribute: 'newPeriod',
          value: String(currentValue)
        });
      }
    });

    // Verificar cambios en sonda
    this.device?.sensors?.forEach((sensor, index) => {
      const key = `sonda${index + 1}`;

      const currentValue = this.stackForm.get(key)?.value;
      const initialValue = this.initialFormValue[key];

      if (currentValue !== initialValue) {
        changes.push({
          id: sensor.id,
          type: '1', // SENSOR
          attribute: 'newProbeConnected',
          value: String(currentValue)
        });
      }
    });


    return changes;
  }

  hasFormChanged(): boolean {
    return JSON.stringify(this.stackForm.value) !== JSON.stringify(this.initialFormValue);
  }

  private deepCopy(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  hasNewPeriods(): boolean {
    // Verifica si el dispositivo principal tiene un newPeriod
    const mainHasNew = !!this.device?.extraInfo?.newPeriod?.value;

    // Verifica si alguno de los sensores tiene un newPeriod
    const sensorsHaveNew = this.device?.sensors?.some(
      sensor => !!sensor.extraInfo?.newPeriod?.value
    ) ?? false;

    return mainHasNew || sensorsHaveNew;
  }

  getEstimatedPeriod(): number {
    return parseInt(this.device?.extraInfo?.period?.value || '30', 10);
  }

  get isBluetoothMode(): boolean {
    // Asumiendo que tienes device?.extraInfo?.isActived?.value indicando Bluetooth
    // O puedes usar otro campo, como device?.connectivity === 'ble'
    return this.device?.extraInfo?.isActived?.value === 'false';
  }


  private updateFormStateByConnectivity() {
    if (!this.stackForm) return;
    if (this.isBluetoothMode) {
      this.stackForm.get('sendPeriod')?.disable({ emitEvent: false });
      this.stackForm.get('enableAutoCorrection')?.disable({ emitEvent: false });
      this.stackForm.get('sendTime')?.disable({ emitEvent: false });
      this.device?.sensors?.forEach((_, i) => {
        this.stackForm.get('node' + (i + 1))?.disable({ emitEvent: false });
      });
    } else {
      this.stackForm.get('sendPeriod')?.enable({ emitEvent: false });
      this.stackForm.get('enableAutoCorrection')?.enable({ emitEvent: false });
      this.stackForm.get('sendTime')?.enable({ emitEvent: false });
      this.device?.sensors?.forEach((_, i) => {
        this.stackForm.get('node' + (i + 1))?.enable({ emitEvent: false });
      });
    }
  }

private normalizeVersion(v?: string): number[] {
  const clean = (v ?? '0.0.0').trim().replace(/^v/i, '');
  return clean.split('.').map(x => parseInt(x, 10)).map(n => (Number.isFinite(n) ? n : 0));
}

private compareSemver(a?: string, b?: string): number {
  const pa = this.normalizeVersion(a);
  const pb = this.normalizeVersion(b);

  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}
//mostrar la columna solo si todos los sensores cumplen con la versión
get showSondaColumn(): boolean {
  const sensors = this.device?.sensors ?? [];
  if (!sensors.length) return false;

  return sensors.every(s => {
    const v = s?.extraInfo?.softwareVersion?.value;
    return !!v && this.compareSemver(v, '1.5.0.0') >= 0;
  });
}


}


